(ns frontend.views.public
  (:require
   [common.css             :as css]
   [common.filter-controls :as filter-controls]
   [common.util            :as u]
   [opengb.i18n            :refer [Tr tr]]
   [reagent.dom.client     :as rg-dom-client]
   [reagent.dom.server     :as rg-dom-server]
   [re-frame.core          :as rf]
   [shadow.graft           :as graft]
   [tailwind-hiccup.core   :refer [tw]]))

(defn- scroll-to-details
  []
  (js/setTimeout
   (fn []
     (let [selector  "#building-details-view"
           container (.querySelector js/document selector)]
       (.scrollIntoView container (clj->js {:behavior "smooth"}))))
   200))

(defmethod graft/scion (u/->scion-id :frontend.views.public/scroll-to-details)
  [_data _container]
  (scroll-to-details))

; "Replaces the building details view with content for provided building-id."
(rf/reg-fx :frontend.views.public/update-building-details-view
           (fn update-building-details-view
             [[building-id js-coords]]
             (let [request-url (str "/home/uuid/" building-id)]
               (-> (js/fetch request-url (clj->js {:headers {"x-opengb-request-fragment" true}}))
                   (.then (fn [response]
                            (let [friendly-id (.get (. response -headers) "x-opengb-building-friendly-id")
                                  new-url     (str "/home/" friendly-id (.. js/window -location -search))]
                              (.pushState js/history nil, nil, new-url))
                            (.text response)))
                   (.then (fn [body]
                            (let [selector  "#building-details-view"
                                  container (.querySelector js/document selector)]
                              (set! container -outerHTML body)
                              (when-let [heading (.querySelector js/document "#scorecard-heading")]
                                (.focus heading #js {:preventScroll true}))
                              (scroll-to-details)
                              (rf/dispatch [:frontend.views.map/focus-on-building js-coords]))))))))

; "Closes the building details view"
(rf/reg-fx :frontend.views.public/close-building-details-view
           (fn close-building-details-view []
             (let [selector  "#building-details-view"
                   container (.querySelector js/document selector)
                   body (rg-dom-server/render-to-string [:div#building-details-view])]
               (.pushState js/history nil, nil, (str "/" (.. js/window -location -search)))
               (set! container -outerHTML body)
               (when-let [search (.querySelector js/document "#search-input")]
                 (.focus search)))))

;; Selected Building

(rf/reg-event-fx :frontend.views.public/set-selected-building
                 (fn [{:keys [db] :as _cofx} [_ building-id js-coords]]
                   {:db (assoc db :frontend.views.public/selected-building building-id)
                    :frontend.views.public/update-building-details-view [building-id js-coords]}))

(defn ^:export set-selected-building
  "Dispatches :frontend.views.public/set-selected-building. The function is
  exported function so js can call into re-frame."
  [building-id js-coords]
  (rf/dispatch [:frontend.views.public/set-selected-building building-id js-coords]))

(rf/reg-event-fx :frontend.views.public/clear-selected-building
                 (fn [{:keys [db] :as _cofx} _]
                   {:db (dissoc db :frontend.views.public/selected-building)
                    :frontend.views.public/close-building-details-view nil}))

(defn ^:export clear-selected-building
  "Dispatches :frontend.views.public/clear-selected-building. The function is
  exported function so js can call into re-frame."
  []
  (rf/dispatch [:frontend.views.public/clear-selected-building]))

;; Filters

(defn- location->params
  "The current URL's query params, keyed the way a ring request keys them."
  []
  (-> (js/URLSearchParams. (.. js/window -location -search))
      (js/Object.fromEntries)
      (js->clj :keywordize-keys true)))

(defn params->current-filters
  "The selections `params` make among `filters`, the configured filters keyed by
  filter key."
  [params filters]
  (filter-controls/params->selections params (update-vals filters :options)))

(defn- with-selections!
  [^js/URLSearchParams search selections]
  (doseq [[filter-key selection] selections]
    (.set search (name (filter-controls/param-key filter-key)) selection))
  search)

(defn- without-filters!
  [^js/URLSearchParams search filter-keys]
  (doseq [filter-key filter-keys]
    (.delete search (name (filter-controls/param-key filter-key))))
  search)

(defn- ->query
  [^js/URLSearchParams search]
  (let [params (.toString search)]
    (if (seq params) (str "?" params) "")))

; "Writes the current filters into the page's URL and into the links that carry them elsewhere"
(rf/reg-fx :frontend.views.public/carry-filters
           (fn carry-filters
             [{:keys [filter-keys selections]}]
             (let [;; The page's own url, edited in place to make the query params
                   ;; reflect the current filter selections.
                   page-query (-> (js/URLSearchParams. (.. js/window -location -search))
                                  (without-filters! filter-keys)
                                  (with-selections! selections)
                                  (->query))
                   ;; The url of other links that filter menu items may point to.
                   ;; This link url only gets the params set to the current filter
                   ;; selection. Any other params that may be present on the current
                   ;; page are not added to the link.
                   link-query (-> (js/URLSearchParams.)
                                  (with-selections! selections)
                                  (->query))]
               (.replaceState js/history nil "" (str (.. js/window -location -pathname) page-query))
               ;; Points the menu's filter-carrying links at the current selections
               ;; now rather than when one is clicked, so that hovering, copying
               ;; the link address and opening it in a new tab all get them too.
               (doseq [link (array-seq (.querySelectorAll
                                        js/document
                                        (str "a[" (name u/link-with-filters-attr) "]")))]
                 (set! (.-search link) link-query)))))

(defn- carrying-filters
  [db]
  {:db db
   :frontend.views.public/carry-filters
   {:filter-keys (keys (:frontend.views.public/filters db))
    :selections  (:frontend.views.public/current-filters db)}})

(rf/reg-event-fx :frontend.views.public/update-filter
                 (fn [{:keys [db] :as _cofx} [_ filter-key selection]]
                   (carrying-filters
                    (-> db
                        (assoc-in [:frontend.views.public/current-filters filter-key] selection)
                        (dissoc :frontend.views.public/announce-filters-reset?)))))

(rf/reg-event-fx :frontend.views.public/clear-filter
                 (fn [{:keys [db] :as _cofx} [_ filter-key]]
                   (carrying-filters
                    (-> db
                        (update :frontend.views.public/current-filters dissoc filter-key)
                        (dissoc :frontend.views.public/announce-filters-reset?)))))

(rf/reg-event-fx :frontend.views.public/reset-filters
                 (fn [{:keys [db] :as _cofx} _]
                   (carrying-filters
                    (-> db
                        (dissoc :frontend.views.public/current-filters)
                        (assoc :frontend.views.public/announce-filters-reset? true)))))

(rf/reg-sub :frontend.views.public/current-filters
            (fn [db _]
              (:frontend.views.public/current-filters db)))

(rf/reg-sub :frontend.views.public/current-filter-selection
            (fn [db [_ filter-key]]
              (get-in db [:frontend.views.public/current-filters filter-key])))

(rf/reg-sub :frontend.views.public/any-filters-applied?
            (fn [db _]
              (boolean (seq (:frontend.views.public/current-filters db)))))

(rf/reg-sub :frontend.views.public/announce-filters-reset?
            (fn [db _]
              (:frontend.views.public/announce-filters-reset? db)))

(defn filter:on-change
  "Updates current filter when selection changes."
  [filter-key event]
  (let [selection (-> event .-target .-selectedOptions (aget 0) .-value)]
    (if (= selection "all")
      (rf/dispatch [:frontend.views.public/clear-filter  filter-key])
      (rf/dispatch [:frontend.views.public/update-filter filter-key selection]))))

(defn filter:Dropdown
  "Dropdown to select map filter options."
  [{:keys [filter-key options]}]
  (let [*current   (rf/subscribe [:frontend.views.public/current-filter-selection filter-key])
        all-option (first options)]
    [filter-controls/Dropdown
     {:title        [Tr (filter-controls/title-key filter-key)]
      :active?      (some? @*current)
      :options      (mapv (fn [option]
                            {:value (if (= option all-option) "all" option)
                             :label (tr (filter-controls/option-label-key filter-key option)
                                        {:default-str option})})
                          options)
      :select-props {:value     (or @*current all-option)
                     :on-change (partial filter:on-change filter-key)}}]))

(defn filter:ResetAnnouncement
  "Reports a reset to assistive technology: on screen the dropdowns snapping back
  to their 'all' option says it happened, and nothing else does."
  []
  (let [announce? @(rf/subscribe [:frontend.views.public/announce-filters-reset?])]
    [:p (tw [:sr-only] {:role "status" :aria-live "polite"})
     (when announce?
       [Tr :frontend.views.public/filters-reset])]))

(defn filter:ResetButton
  "Button to clear all current filters, with the region that reports the clearing.
  Unavailable while there is nothing to clear."
  []
  (let [any-filters? @(rf/subscribe [:frontend.views.public/any-filters-applied?])]
    [:div
     ;; aria-disabled rather than disabled: the browser blurs a disabled element,
     ;; so pressing this by keyboard would drop focus to the top of the document
     ;; at the moment it turns unavailable. This keeps it focusable, and announced
     ;; as unavailable, and the press does nothing.
     [:button (tw css/reset-control
                  ["aria-disabled:bg-neutral-9" "aria-disabled:text-neutral-3" "aria-disabled:font-normal"
                   "aria-disabled:hover:bg-neutral-9" "aria-disabled:cursor-not-allowed"]
                  {:aria-disabled (if any-filters? "false" "true")
                   :on-click      #(when any-filters?
                                     (rf/dispatch [:frontend.views.public/reset-filters]))})
      [Tr :frontend.views.public/reset-filters]]
     [filter:ResetAnnouncement]]))

(defn Filters
  "Filters for the map."
  [{:keys [:frontend.views.public/filters] :as _filters-config}]
  [:<>
   [:details (tw [:md:hidden :p-4 :space-y-2])
    [:summary
     [:h2 (tw css/h2 [:inline :cursor-pointer])
      [Tr :frontend.views.public/map-filters]]]
    (for [[filter-key filter-config] filters]
      ^{:key filter-key}
      [filter:Dropdown filter-config])
    [filter:ResetButton]]
   [:div (tw css/filter-panel [:hidden :md:block])
    [:h2 (tw css/h2) [Tr :frontend.views.public/map-filters]]
    (for [[filter-key filter-config] filters]
      ^{:key filter-key}
      [filter:Dropdown filter-config])
    [filter:ResetButton]]])

(rf/reg-event-fx :frontend.views.public/setup-db
                 (fn [{:keys [db] :as _cofx} [_ config]]
                   {:db (merge db config
                               {:frontend.views.public/current-filters
                                (params->current-filters
                                 (location->params)
                                 (:frontend.views.public/filters config))})}))

(defmethod graft/scion (u/->scion-id :frontend.views.public/initalize-filters)
  [filters-config container]
  (let [root (rg-dom-client/create-root container)]
    (rf/dispatch [:frontend.views.public/setup-db filters-config])
    (rg-dom-client/render root [Filters filters-config])))
